"use client";
//import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import React from "react";
import {
  Users,
  IndianRupeeIcon,
  Calendar,
  CalendarDaysIcon,
  MapPin,
  Globe,
  Building,
  Briefcase,
  Mail,
  Phone,
} from "lucide-react";
//import Modal from '@/components/ui/modal';
import { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogClose } from "@/components/ui/dialog";
import { format } from "date-fns";

interface EventCardProps {
  id: number;
  name: string;
  type: string;
  status: "requested" | "planned" | "inprogress" | "completed" | "cancelled" | "approved";
  startDate?: Date | string;
  endDate?: Date | string;
  location: string;
  participants: number;
  budget: number;
  description: string;

  requesterName: string;
  mobileNum: string;
  emailId: string;
  department: string;
  costCenter: string;
  bizEntity: string;  

  nature: string;
  isEventDateFlexible: boolean;

  // 3. Residential Events
  numSingleRooms: number;
  numDoubleRooms: number;
  groundTransportReqd: boolean;
  numVipRooms: number;

  // 4. Non-residential Events
  // are these 2 field redundant as they already exist in event information?
  nonResStartDate?: Date | string;
  nonResEndDate?: Date | string;
  eventTiming: string;
  eventDuration: "First Half" | "Second Half" | "Full Day" | ""; // First half / Second half / full day (9 am - 5 pm)

  // 4. Event Requirements
  ohpProjector: boolean; // yes / No
  seatingStyle: "Round" | "Theatre" | "Classroom" | ""; // Round / Theatre / classroom
  flipChart: boolean; 
  breakOutRooms: boolean;
  celebration: "DJ" | "Liveband" | "AV" | ""; // DJ / Liveband / AV
  giftsToParticipants: string;
  othersEventRequirements: string;

  // 5. Snack & Meal
  mornCoffeeBreakTiming: string;
  eveTeaBreakTiming: string;
  numOfVegMeals: number;
  numOfNonVegMeals: number;
  isDinnerRequired: boolean;
  othersSnacknMeals: string;

  // 6. Budgets
  eventBudget: number;
  airBudget: number;
  stayBudget: number;
  railRoadBudget: number;
  avDjBudget: number;
  giftBudget: number;
  priorNightSetupBudget: number;
}

// TBD - these 2 arrays can be merged (statusColors and StatusLabel)
const statusColors = {
  requested: "bg-black-100 text-black-800 border-black-200",
  planned: "bg-yellow-100 text-yellow-800 border-yellow-200",
  "inprogress": "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  approved: "bg-purple-100 text-purple-800 border-purple-200",
};

const statusLabels = {
  requested: "Requested",
  planned: "Planned",
  "inprogress": "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
  approved: "Approved",
};

export function EventCard({
  id,
  name,
  type,
  status,
  startDate,
  endDate,
  location,
  participants,
  budget,
  description,
  requesterName,
  mobileNum,
  emailId,
  department,
  costCenter,
  bizEntity,

  nature,
  isEventDateFlexible,

  // 3. Residential Events
  numSingleRooms,
  numDoubleRooms,
  groundTransportReqd,
  numVipRooms,

  // 4. Non-residential Events
  // are these 2 field redundant as they already exist in event information?
  nonResStartDate,
  nonResEndDate,
  eventTiming,
  eventDuration, // First half / Second half / full day (9 am - 5 pm)

  // 4. Event Requirements
  ohpProjector, // yes / No
  seatingStyle, // Round / Theatre / classroom
  flipChart, 
  breakOutRooms,
  celebration, // DJ / Liveband / AV
  giftsToParticipants,
  othersEventRequirements,

  // 5. Snack & Meal
  mornCoffeeBreakTiming,
  eveTeaBreakTiming,
  numOfVegMeals,
  numOfNonVegMeals,
  isDinnerRequired,
  othersSnacknMeals,

  // 6. Budgets
  eventBudget,
  airBudget,
  stayBudget,
  railRoadBudget,
  avDjBudget,
  giftBudget,
  priorNightSetupBudget,

}: EventCardProps) {
  
  const [showModal, setShowModal] = useState(false);

  const handleOpen = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  // TBD - move all the className to the CSS file
  return (
    <div
      key={id}
      className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
      onClick={handleOpen} style={{cursor: 'pointer'}}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            
            {/* name */}
            <h3 className="font-semibold text-lg">{name}</h3>

             {/* status */}
            <Badge className={cn("text-xs", statusColors[status])}>
              {statusLabels[status]}
            </Badge>

          </div>
          {/* type */}
          <p className="text-foreground text-sm mb-3 text-slate-500">
            {type}
          </p>
          {/* description */}
          <p className="text-foreground text-sm mb-3 text-slate-500">
            {description}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-2 gap-4 text-sm">
        
        {/* Start Date */}
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-foreground text-slate-400" />
          {/*<span>{new Date(startDate).toLocaleDateString()}</span>*/}
          <span> 
            {startDate ? format(new Date(startDate), "yyyy-MM-dd") : "No start date"}
          </span>
        </div>
        
        {/* End Date */}
        <div className="flex items-center gap-2">
          <CalendarDaysIcon className="h-4 w-4 text-foreground text-slate-400" />
          {/*<span>{ endDate ? new Date(endDate).toLocaleDateString(): "1 day event"}</span>*/}
          <span>{endDate ? format(new Date(endDate), "yyyy-MM-dd") : "1 day event"}</span>
        </div>

        {/* Venue */}
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-foreground text-slate-400" />
          <span>{location}</span>
        </div>

        {/* Participants */}
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-foreground text-slate-400" />
          <span>{participants} participants</span>
        </div>

         {/* Budget */}
        <div className="flex items-center gap-2">
          <IndianRupeeIcon className="h-4 w-4 text-foreground text-slate-400" />
          <span>{budget.toLocaleString()}</span>
        </div>

         {showModal && (
          <Dialog open={showModal} onOpenChange={handleClose} >
            <DialogContent
              showCloseButton={false}
              className="max-h-[90vh] max-w-4xl overflow-hidden p-0"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex max-h-[90vh] flex-col">
                <div className="border-b bg-muted/30 px-6 py-5 pr-16">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <DialogTitle className="text-lg">{name}</DialogTitle>
                      <p className="mt-1 text-sm text-muted-foreground">{type}</p>
                    </div>
                    <Badge className={cn("shrink-0 text-xs", statusColors[status])}>
                      {statusLabels[status]}
                    </Badge>
                  </div>
                  {description && (
                    <p className="mt-3 max-w-3xl text-sm leading-6 text-muted-foreground">
                      {description}
                    </p>
                  )}
                </div>

                <div className="space-y-6 overflow-y-auto px-6 py-5 text-sm leading-5 text-muted-foreground">

              {/* Start Date */}
              <section>
                <h3 className="mb-3 text-sm font-semibold text-foreground">Event overview</h3>
                <div className="grid grid-cols-1 gap-3 rounded-lg border bg-muted/20 p-4 md:grid-cols-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-foreground text-slate-400" />
                  {/*<span>{new Date(startDate).toLocaleDateString()}</span>*/}
                  <span> 
                    {startDate ? format(new Date(startDate), "yyyy-MM-dd") : "No start date"}
                  </span>
                </div>
                
                {/* End Date */}
                <div className="flex items-center gap-2">
                  <CalendarDaysIcon className="h-4 w-4 text-foreground text-slate-400" />
                  {/*<span>{ endDate ? new Date(endDate).toLocaleDateString(): "1 day event"}</span>*/}
                  <span>{endDate ? format(new Date(endDate), "yyyy-MM-dd") : "1 day event"}</span>
                </div>

                {/* Venue */}
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-foreground text-slate-400" />
                  <span>{location}</span>
                </div>

                {/* Participants */}
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-foreground text-slate-400" />
                  <span>{participants} participants</span>
                </div>

                {/* Budget */}
                <div className="flex items-center gap-2">
                  <IndianRupeeIcon className="h-4 w-4 text-foreground text-slate-400" />
                  <span>{budget.toLocaleString()}</span>
                </div>
                </div>
              </section>

              <section className="rounded-lg border bg-muted/20 p-4">
                <h3 className="mb-3 text-sm font-semibold text-foreground">Requester details</h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Requester Name: {requesterName}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Mobile Number: {mobileNum}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Email ID: {emailId}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Department: {department}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Cost Center: {costCenter}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Business Entity: {bizEntity}</span>
                  </div>
                </div>
              </section>

              {/* Nature of Event */}
              <section className="rounded-lg border bg-muted/20 p-4">
                <h3 className="mb-3 text-sm font-semibold text-foreground">Event planning</h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-foreground text-slate-400" />
                <span> Nature of Event: {nature}</span>
              </div>

              {/* Is Event Date Flexible */}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-foreground text-slate-400" />
                <span> Is Event Date Flexible: {isEventDateFlexible ? "Yes" : "No"}</span>
              </div>

              {/* Conditional rendering based on nature of event */}
              {nature === "Residential" && (
                <>
                  {/* Number of Single Rooms */}
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Number of Single Rooms: {numSingleRooms}</span>
                  </div>

                  {/* Number of Double Rooms */}
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Number of Double Rooms: {numDoubleRooms}</span>
                  </div>

                  {/* Ground Transport Required */}
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Ground Transport Required: {groundTransportReqd ? "Yes" : "No"}</span>
                  </div>

                  {/* Number of VIP Rooms */}
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Number of VIP Rooms: {numVipRooms}</span>
                  </div>
                </>
              )}

              {nature === "Non-Residential" && (
                <>
                  {/* Non-Residential Event Start Date */}
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Non-Residential Event Start Date: {nonResStartDate ? format(new Date(nonResStartDate), "yyyy-MM-dd") : "N/A"}</span>
                  </div>

                  {/* Non-Residential Event End Date */}
                  <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Non-Residential Event End Date: {nonResEndDate ? format(new Date(nonResEndDate), "yyyy-MM-dd") : "N/A"}</span>
                  </div>

                  {/* Event Timing */}
                  <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Event Timing: {eventTiming}</span>
                  </div>

                  {/* Event Duration */}
                  <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-foreground text-slate-400" />
                    <span> Event Duration: {eventDuration}</span>
                  </div>
                </>
              )}
                </div>
              </section>

              {/* Event Requirements */}
              <section className="rounded-lg border bg-muted/20 p-4">
                <h3 className="mb-3 text-sm font-semibold text-foreground">Venue requirements</h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-foreground text-slate-400" />
                  <span> OHP Projector: {ohpProjector ? "Yes" : "No"}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Seating Style: {seatingStyle || "N/A"}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Flip Chart: {flipChart ? "Yes" : "No"}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Breakout Rooms: {breakOutRooms ? "Yes" : "No"}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Celebration: {celebration || "N/A"}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Gifts to Participants: {giftsToParticipants || "N/A"}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Other Event Requirements: {othersEventRequirements || "N/A"}</span>
                </div>
                </div>
              </section>

              {/* Snack & Meal Details */}
              <section className="rounded-lg border bg-muted/20 p-4">
                <h3 className="mb-3 text-sm font-semibold text-foreground">Meals and breaks</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Morning Coffee Break Timing: {mornCoffeeBreakTiming || "N/A"}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Evening Tea Break Timing: {eveTeaBreakTiming || "N/A"}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Number of Veg Meals: {numOfVegMeals}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Number of Non-Veg Meals: {numOfNonVegMeals}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Is Dinner Required: {isDinnerRequired ? "Yes" : "No"}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Other Snacks & Meals: {othersSnacknMeals || "N/A"}</span>
                </div>
                </div>
              </section>

              {/* Budget Details */}
              <section className="rounded-lg border bg-muted/20 p-4">
                <h3 className="mb-3 text-sm font-semibold text-foreground">Budget breakdown</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex items-center gap-2">
                  <IndianRupeeIcon className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Event Budget: Rs. {eventBudget.toLocaleString()}</span>
                </div>

                <div className="flex items-center gap-2">
                  <IndianRupeeIcon className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Air Budget: Rs. {airBudget.toLocaleString()}</span>
                </div>

                <div className="flex items-center gap-2">
                  <IndianRupeeIcon className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Stay Budget: Rs. {stayBudget.toLocaleString()}</span>
                </div>

                <div className="flex items-center gap-2">
                  <IndianRupeeIcon className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Rail/Road Budget: Rs. {railRoadBudget.toLocaleString()}</span>
                </div>

                <div className="flex items-center gap-2">
                  <IndianRupeeIcon className="h-4 w-4 text-foreground text-slate-400" />
                  <span> AV/DJ Budget: Rs. {avDjBudget.toLocaleString()}</span>
                </div>

                <div className="flex items-center gap-2">
                  <IndianRupeeIcon className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Gift Budget: Rs. {giftBudget.toLocaleString()}</span>
                </div>

                <div className="flex items-center gap-2">
                  <IndianRupeeIcon className="h-4 w-4 text-foreground text-slate-400" />
                  <span> Prior Night Setup Budget: Rs. {priorNightSetupBudget.toLocaleString()}</span>
                </div>
                </div>
              </section>
                </div>

                <div className="border-t bg-background px-6 py-4">
                  <DialogClose className="inline-flex h-11 w-full items-center justify-center rounded-md border border-primary bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    Close
                  </DialogClose>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )} 
      </div>
    </div>
  );
}
